import { useMemo } from "react";
import { useIntl } from "react-intl";
import { usePropertyOptional } from "../../../../../components/property-provider";
import { useIssueFormatter } from "../../../application/hooks/useIssueFormatter";
import { CategoryLimit } from "../../../domain/entities/CategoryLimit";
import { useCategoryLimitsContext } from "../../context/CategoryLimitsContext";
import { CategoryLimitsLabels } from "../../formatters/CategoryLimitsLabels";
import {
  mapCategoryLimitsFormDataToDomain,
  mapCategoryLimitsToFormData,
  mapCategoryLimitToFormData,
} from "../../mappers/category-limits.mapper";
import { CategoryLimitsFormData } from "../../types/CategoryLimitsTypes";
import {
  buildCategoryLimitBlockViewModel,
  CategoryLimitBlockViewModel,
  CategoryLimitsErrorSchema,
  collectBlockErrors,
  parseLimitOption,
} from "./categoryLimitBlock.viewModel";

export interface CategoryLimitBlockActions {
  /** Turning a block off keeps its values. */
  readonly onToggle: (enabled: boolean) => void;
  readonly onIncludedChange: (value: string) => void;
  readonly onMaxChange: (value: string) => void;
  readonly onTick: (extraTicker: string, ticked: boolean) => void;
}

export interface CategoryLimitBlockModel {
  readonly view: CategoryLimitBlockViewModel;
  readonly actions: CategoryLimitBlockActions;
}

interface UseCategoryLimitsFieldProps {
  readonly formData?: CategoryLimitsFormData;
  readonly errorSchema?: unknown;
  readonly onChange: (formData: CategoryLimitsFormData) => void;
}

/**
 * State and actions of the *Limits per category* rjsf field. `formData` holds only what the
 * user edits; lines, names and prices come from the section in the context. Every edit goes
 * through the `CategoryLimit` aggregate and clears that block's server issues.
 */
export function useCategoryLimitsField({
  formData,
  errorSchema,
  onChange,
}: UseCategoryLimitsFieldProps): { visible: boolean; blocks: readonly CategoryLimitBlockModel[] } {
  const intl = useIntl();
  const issueFormatter = useIssueFormatter();
  const currency = usePropertyOptional()?.info?.defaultCurrency || undefined;
  const { limits, accommodationName, serverIssues, clearServerIssues } = useCategoryLimitsContext();

  const labels = useMemo(() => new CategoryLimitsLabels(intl, currency), [intl, currency]);

  return useMemo(() => {
    if (!limits?.isSectionVisible()) {
      return { visible: false, blocks: [] };
    }
    const section = mapCategoryLimitsFormDataToDomain(limits, formData);
    const format = issueFormatter.format.bind(issueFormatter);

    const commit = (next: CategoryLimit) => {
      clearServerIssues(next.category);
      onChange({
        ...mapCategoryLimitsToFormData(section),
        [next.category]: mapCategoryLimitToFormData(next),
      });
    };

    const blocks = section.blocks.map(block => ({
      view: buildCategoryLimitBlockViewModel({
        block,
        errors: collectBlockErrors(
          block.category,
          errorSchema as CategoryLimitsErrorSchema,
          serverIssues,
          format
        ),
        accommodationName,
        labels,
        format,
      }),
      actions: {
        onToggle: (enabled: boolean) => commit(block.withEnabled(enabled)),
        onIncludedChange: (value: string) => commit(block.withIncluded(parseLimitOption(value))),
        onMaxChange: (value: string) => commit(block.withMax(parseLimitOption(value))),
        onTick: (extraTicker: string, ticked: boolean) =>
          commit(block.withTicked(extraTicker, ticked)),
      },
    }));
    return { visible: true, blocks };
  }, [
    limits,
    formData,
    errorSchema,
    serverIssues,
    accommodationName,
    labels,
    issueFormatter,
    clearServerIssues,
    onChange,
  ]);
}
